import {
  AttributeGroupDraft,
  ProductModifierGroupDraft,
  ProductVariantAttributeValueDraft,
  ProductVariantEditor,
  VariationPreviewRow,
} from '@features/catalog/models/product-form-draft.models';
import {
  ProductSupplySourceInputValue,
  ProductVariantUpsertInput,
} from '@features/catalog/models/product.models';
import {
  CreateProductBundleInput,
  CreateProductBundleVariantAttributeInput,
  CreateProductBundleVariantInput,
} from '@features/catalog/models/product-input.models';
import { dedupeCategoryIds } from '@features/catalog/utils/product-category-assignment.utils';
import {
  bundleVariantsForProductStructure,
  resolveProductPublicationFlags,
} from '@features/catalog/utils/product-form-publication.utils';
import { isManagedMediaDeliveryUrl } from '@core/media/utils/media-image.util';
import { normalizeDescriptionForApi } from '@shared/utils/rich-text.sanitizer';
import { validateModifierGroupsDraft } from '@features/catalog/utils/product-modifier-groups.validation';
import {
  mapPreparationEditorValueToProductBundleFields,
  preparationEditorValueFromForm,
} from '@shared/ui/preparation-editor/preparation-editor.utils';
import { mapModifierGroupsToBundleInput } from '@features/catalog/utils/product-form-modifier-draft.mapper';

export interface ProductBundleFormRawValue {
  name: string;
  price: number;
  productStructure: 'simple' | 'variable';
  description: string;
  imageUrl: string;
  publicVisible: boolean;
  posVisible: boolean;
  catalogBestSellerEnabled?: boolean;
  catalogBestSellerPosition?: number | null;
  catalogBadgeText?: string;
  fulfillmentLeadTimeMode?: 'inherit' | 'override';
  fulfillmentLeadTimeDays?: number | null;
  fulfillmentReadyTime?: string | null;
  sendToKitchen?: boolean;
  prepRequiresMode?: 'inherit' | 'override';
  prepTimeMinutes?: number | null;
  /** Permite elegir este producto como "segundo sabor" en mitad y mitad (solo variables). */
  allowHalfAndHalf?: boolean;
  active: boolean;
  categoryIds: string[];
  sku: string;
  stockQuantity: number | null;
  controlInventory?: boolean;
  /** Estado al cargar el producto (para enviar disable solo si corresponde). */
  inventoryManagedAtLoad?: boolean;
  /**
   * Vínculo de inventario presente al cargar aunque el control no esté operable
   * (item inactivo / sin track_stock). Obliga a enviar `enabled: false` para sanear.
   */
  inventoryItemIdAtLoad?: string | null;
  /**
   * Peso/dimensiones para envío nacional. `createProductBundle`/`updateProductBundle` no los
   * aceptan (solo `updateProduct`), por eso no se incluyen en `CreateProductBundleInput`: el
   * coordinador de guardado (`ProductFormSaveCoordinatorService`) los persiste en una llamada aparte.
   */
  weightGrams?: number | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  /**
   * "También es insumo" + proveedores seleccionados. `createProductBundle`/
   * `updateProductBundle` tampoco los aceptan: el coordinador de guardado los
   * persiste con `syncProductSupplySources` en una llamada aparte, igual que
   * peso/dimensiones.
   */
  isSupplyItem?: boolean;
  supplySources?: readonly ProductSupplySourceInputValue[];
}

export const VARIANT_ATTRIBUTES_INCOMPLETE_ERROR =
  'Cada variación debe estar vinculada a un valor de atributo.';

function buildVariantAttributePairs(
  attributeValues: ProductVariantAttributeValueDraft[]
): CreateProductBundleVariantAttributeInput[] {
  return attributeValues
    .map((item) => ({
      definitionName: item.definitionName.trim(),
      value: item.value.trim(),
    }))
    .filter((item) => item.definitionName.length > 0 && item.value.length > 0);
}

function buildVariantAttributePayload(
  variant: ProductVariantEditor,
  knownAttributeValueIds: ReadonlySet<string>,
  isVariable: boolean
): Pick<CreateProductBundleVariantInput, 'attributeValueIds' | 'attributes'> {
  if (!isVariable) {
    return {};
  }

  const attributeValueIds = variant.attributeValues
    .map((value) => value.id.trim())
    .filter((id) => knownAttributeValueIds.has(id));
  const attributePairs = buildVariantAttributePairs(variant.attributeValues);
  const allValuesMappedById =
    attributeValueIds.length > 0 && attributeValueIds.length === variant.attributeValues.length;

  if (allValuesMappedById) {
    return { attributeValueIds };
  }

  if (attributePairs.length === 0 || attributePairs.length < variant.attributeValues.length) {
    throw new Error(VARIANT_ATTRIBUTES_INCOMPLETE_ERROR);
  }

  return { attributes: attributePairs };
}

function firstAttributePairFromStructuredValues(
  values: ProductVariantAttributeValueDraft[]
): string | null {
  const firstValue = values.find(
    (value) => value.definitionName.trim().length > 0 && value.value.trim().length > 0
  );

  return firstValue ? `${firstValue.definitionName.trim()}: ${firstValue.value.trim()}` : null;
}

export function buildVariantUpsertInput(
  variant: ProductVariantEditor,
  knownAttributeValueIds?: ReadonlySet<string>
): ProductVariantUpsertInput {
  const rawImageUrl = variant.imageUrl?.trim() ?? '';
  const imageUrl = !rawImageUrl
    ? null
    : isManagedMediaDeliveryUrl(rawImageUrl)
      ? rawImageUrl
      : null;

  const attributePayload = knownAttributeValueIds
    ? buildVariantAttributePayloadForUpsert(variant.attributeValues, knownAttributeValueIds)
    : {
        attributeValueIds: variant.attributeValues
          .map((item) => item.id)
          .filter((id) => id.trim().length > 0),
      };

  return {
    id: variant.isNew ? null : variant.id,
    name: variant.name.trim() || null,
    sku: variant.sku.trim() || null,
    externalReference: variant.externalReference.trim() || null,
    imageUrl,
    price: Number(variant.price),
    stockQuantity: variant.stockQuantity,
    position: variant.position,
    active: variant.active,
    ...attributePayload,
  };
}

export function buildVariantReconcileUpsertInput(
  row: VariationPreviewRow,
  knownAttributeValueIds: ReadonlySet<string>
): Pick<ProductVariantUpsertInput, 'attributeValueIds' | 'attributes'> {
  return buildVariantAttributePayloadForUpsert(row.attributeValues, knownAttributeValueIds);
}

function buildVariantAttributePayloadForUpsert(
  attributeValues: readonly ProductVariantAttributeValueDraft[],
  knownAttributeValueIds: ReadonlySet<string>
): Pick<ProductVariantUpsertInput, 'attributeValueIds' | 'attributes'> {
  const attributeValueIds = attributeValues
    .map((value) => value.id.trim())
    .filter((id) => knownAttributeValueIds.has(id));

  if (attributeValueIds.length > 0 && attributeValueIds.length === attributeValues.length) {
    return { attributeValueIds };
  }

  const attributes = attributeValues
    .map((item) => ({
      definitionName: item.definitionName.trim(),
      value: item.value.trim(),
    }))
    .filter((item) => item.definitionName.length > 0 && item.value.length > 0);

  if (attributes.length === 0 || attributes.length < attributeValues.length) {
    throw new Error(VARIANT_ATTRIBUTES_INCOMPLETE_ERROR);
  }

  return { attributes };
}

export function buildCreateBundleInput(
  raw: ProductBundleFormRawValue,
  attributeGroups: AttributeGroupDraft[],
  productVariants: ProductVariantEditor[],
  modifierGroups: ProductModifierGroupDraft[],
  knownAttributeValueIds: ReadonlySet<string>,
  variantUntitledLabel: string,
  lockVersion?: number | null,
  /** Solo se envían campos de anticipación de entrega si el plan de la compañía incluye el módulo. */
  fulfillmentLeadTimeEnabled = false,
  /** Rail Inventario / SyncProductStockControl. false → no enviar inventoryControl (stock básico). */
  inventoryModuleEnabled = true
): CreateProductBundleInput {
  const isVariable = raw.productStructure === 'variable';
  const price = isVariable ? null : Number(raw.price);
  const hasActiveVariants = isVariable && productVariants.some((variant) => variant.active !== false);

  validateModifierGroupsDraft(modifierGroups, hasActiveVariants);

  const attributes = attributeGroups
    .map((group, groupIndex) => ({
      definitionId: group.definitionId?.trim() || undefined,
      name: group.name.trim(),
      position: groupIndex,
      values: group.values
        .map((value) => value.value.trim())
        .filter(Boolean)
        .map((value, valueIndex) => ({ value, position: valueIndex })),
    }))
    .filter((group) => group.name.length > 0);

  const mappedVariants = productVariants.map((variant, index) => {
    const fallbackName =
      firstAttributePairFromStructuredValues(variant.attributeValues) ?? variantUntitledLabel;
    const attributePayload = buildVariantAttributePayload(variant, knownAttributeValueIds, isVariable);
    const stockLockedByCanonical =
      inventoryModuleEnabled && !!variant.inventoryManaged;
    const basicStockOff = !inventoryModuleEnabled && !raw.controlInventory;

    return {
      name: variant.name.trim() || fallbackName,
      sku: variant.sku.trim() || null,
      price: Number(variant.price ?? 0),
      stockQuantity: stockLockedByCanonical || basicStockOff ? null : (variant.stockQuantity ?? null),
      clientKey: variant.id.startsWith('tmp-') || variant.isNew ? variant.id : null,
      id: variant.isNew ? null : variant.id,
      position: variant.position ?? index,
      active: variant.active ?? true,
      imageUrl: isManagedMediaDeliveryUrl(variant.imageUrl) ? variant.imageUrl!.trim() : null,
      ...attributePayload,
    };
  });
  const variants = bundleVariantsForProductStructure(raw.productStructure, mappedVariants);
  const mappedModifierGroups = mapModifierGroupsToBundleInput(modifierGroups, productVariants);
  const publication = resolveProductPublicationFlags(
    raw.active,
    raw.publicVisible,
    raw.posVisible
  );

  const inventoryControl = inventoryModuleEnabled
    ? buildInventoryControlInput(raw, productVariants)
    : undefined;

  const stockLockedByCanonical = inventoryModuleEnabled && !!raw.controlInventory;
  const basicManageStock = !inventoryModuleEnabled ? !!raw.controlInventory : undefined;
  const stockQuantity = (() => {
    if (stockLockedByCanonical) {
      return null;
    }
    if (basicManageStock === false) {
      return null;
    }
    if (basicManageStock === true && !isVariable) {
      return raw.stockQuantity ?? 0;
    }
    return raw.stockQuantity ?? null;
  })();

  return {
    name: raw.name.trim(),
    price,
    description: normalizeDescriptionForApi(raw.description),
    imageUrl: isManagedMediaDeliveryUrl(raw.imageUrl) ? raw.imageUrl.trim() : null,
    sku: raw.sku.trim() || null,
    stockQuantity,
    ...(basicManageStock === undefined ? {} : { manageStock: basicManageStock }),
    inventoryControl,
    active: publication.active,
    publicVisible: publication.publicVisible,
    posVisible: publication.posVisible,
    catalogBestSellerEnabled: raw.catalogBestSellerEnabled ?? false,
    catalogBestSellerPosition: raw.catalogBestSellerEnabled
      ? raw.catalogBestSellerPosition
      : null,
    catalogBadgeText: raw.catalogBadgeText?.trim() || null,
    allowHalfAndHalf: raw.allowHalfAndHalf ?? false,
    ...mapPreparationEditorValueToProductBundleFields(
      preparationEditorValueFromForm({
        sendToKitchen: raw.sendToKitchen ?? false,
        prepRequiresMode: raw.prepRequiresMode,
        prepTimeMinutes: raw.prepTimeMinutes,
        fulfillmentLeadTimeMode: raw.fulfillmentLeadTimeMode,
        fulfillmentLeadTimeDays: raw.fulfillmentLeadTimeDays,
        fulfillmentReadyTime: raw.fulfillmentReadyTime,
      }),
      fulfillmentLeadTimeEnabled
    ),
    categoryIds: dedupeCategoryIds(raw.categoryIds ?? []),
    // Simple no usa atributos: [] pisa asignaciones huérfanas (mismo patrón que variants).
    attributes: isVariable ? (attributes.length ? attributes : undefined) : [],
    variants,
    modifierGroups: mappedModifierGroups.length ? mappedModifierGroups : undefined,
    lockVersion: lockVersion == null ? undefined : lockVersion,
  };
}

function buildInventoryControlInput(
  raw: ProductBundleFormRawValue,
  productVariants: ProductVariantEditor[],
): CreateProductBundleInput['inventoryControl'] {
  const wantEnabled = !!raw.controlInventory;
  const currentlyManaged =
    !!raw.inventoryManagedAtLoad || productVariants.some((variant) => !!variant.inventoryManaged);
  const hasStaleInventoryLink =
    (!!raw.inventoryItemIdAtLoad && !raw.inventoryManagedAtLoad) ||
    productVariants.some((variant) => !!variant.inventoryItemId && !variant.inventoryManaged);

  // Apagar un control que ya estaba activo, o sanear vínculo zombie (item
  // inactivo): siempre hay que avisarle al backend.
  if (!wantEnabled) {
    return currentlyManaged || hasStaleInventoryLink ? { enabled: false } : undefined;
  }

  const isVariable = raw.productStructure === 'variable';
  // Variante nueva/aún sin vincular mientras el control ya está activo: falta
  // crearle su item de inventario, así que sigue siendo un cambio a sincronizar.
  const hasUnlinkedVariants =
    isVariable && productVariants.some((variant) => !variant.inventoryManaged);

  // Ya estaba activo, no se está activando ahora y no hay variantes por vincular:
  // no hubo ningún cambio de inventario (p. ej. solo se editó nombre/precio), así
  // que no se envía `inventoryControl` y el backend no exige permisos de inventario.
  if (currentlyManaged && !hasUnlinkedVariants) {
    return undefined;
  }

  // El umbral de stock bajo no se envía desde Catálogo: se administra por item
  // en Inventario y enviarlo aquí pisaría umbrales configurados allá.
  if (isVariable) {
    return {
      enabled: true,
      variants: productVariants.map((variant) => ({
        variantId: variant.isNew ? null : variant.id,
        clientKey: variant.isNew || variant.id.startsWith('tmp-') ? variant.id : null,
        initialQuantity: variant.inventoryManaged ? null : (variant.stockQuantity ?? 0),
      })),
    };
  }

  return {
    enabled: true,
    initialQuantity: currentlyManaged ? null : (raw.stockQuantity ?? 0),
  };
}
